import { put, call, delay, select } from 'redux-saga/effects';
import { overtimeConstants } from './OvertimeConstants';
import { getCallsheet, addOTShift, resolveWbFailed, postCallLog, getCallLog, getVotReportData, checkOTShiftStatus } from './index';
import { getOTSkillsData } from '../OT-Comp/index';
import * as actions from '../OT-Comp/OvertimeAction';
import { getStationTimestamp } from '../VTO/utils';

const MAX_RETRIES = 3;
const RETRY_DELAY = 1000;

export function* getOvertimeData(action) {
    try {
        yield put({ type: overtimeConstants.SHOW_OVERTIME_LOADER, data: true });
        const response = yield call(getCallsheet, action.params);
        
        // Check for API-level errors returned with HTTP 200
        const responseData = response?.data;
        if (responseData?.errorCode || responseData?.session?.errorCode) {
            const errorMessage = responseData?.message || responseData?.session?.message || 'An error occurred. Please try again.';
            yield put({ 
                type: overtimeConstants.GET_OVERTIME_DATA_FAILED, 
                data: errorMessage
            });
            yield put({ type: overtimeConstants.SHOW_OVERTIME_LOADER, data: false });
            return;
        }

        const data = responseData?.data || responseData || response;
        const sessionToken = data?.session?.sessionToken;
        if (sessionToken) {
            localStorage.setItem('ot_sessionToken', sessionToken);
        }

        yield put({ 
            type: overtimeConstants.GET_OVERTIME_DATA_SUCCESS, 
            data: response.data,
            requestParams: {
                ...(action.params || {}),
                isCurrentCompleted: false,
            }
        });
        
        yield put({ type: overtimeConstants.SHOW_OVERTIME_LOADER, data: false });
       
    } catch (error) {
        yield put({ 
            type: overtimeConstants.GET_OVERTIME_DATA_FAILED, 
            data: error.message || 'Failed to fetch overtime data' 
        });
        yield put({ type: overtimeConstants.SHOW_OVERTIME_LOADER, data: false });
     
    }
}

export function* confirmOvertimeDecision(action) {
    const { employeeId, shiftName, decision, shiftData } = action;
    for(let i = 0; i<3; i++) {
        try {
            yield put({ type: overtimeConstants.SHOW_OVERTIME_LOADER, data: true });
                const requestBody = {
                    empId: employeeId,
                    votRequestId: shiftData?.votRequestId || '',
                    station: shiftData?.station || '',
                    shiftDate: shiftData?.shiftDate || '',
                    otStartTime: shiftData?.otStartTime || '',
                    otEndTime: shiftData?.otEndTime || '',
                    shiftType: shiftData?.shiftType || '',
                    workLocation: shiftData?.workLocation || '',
                    schedBidGroup: shiftData?.schedBidGroup || '',
                    otGroup: shiftData?.otGroup || '',
                    unpaidLunch: shiftData?.unpaidLunch? shiftData?.unpaidLunch : 'N',
                    overlapAllowed: shiftData?.overlapAllowed || 'N',
                    requestType: ({ ACCEPT: 'APPLY', ACCEPTED: 'APPLY', REFUSE: 'REFUSE', REFUSED: 'REFUSE' }[(decision || '').toUpperCase()] || decision),
                    reason: shiftData?.otCode || '',
                    previousTransactionId: shiftData?.previousTransactionId || '',
                    transactionTime: shiftData?.transactionTime || new Date().toISOString(),
                    firstName: shiftData?.firstName || '',
                    lastName: shiftData?.lastName || '',
                    unionSeniority: shiftData?.unionSeniority || '',
                };

                const response = yield call(addOTShift, requestBody);
                if (response?.status === 200 || response?.data?.statusCode === 'SUCCESS' || response?.data?.result?.status === 'SUCCESS') {
                    const station = shiftData?.station || '';
                    let lastTimeStamp = '';
                    try {
                        lastTimeStamp = yield call(getStationTimestamp, station);
                    } catch (e) {
                        lastTimeStamp = new Date().toISOString();
                    }
                    // Extract reqId from /shift API response
                    const resultArr = Array.isArray(response?.data?.result) ? response.data.result : [];
                    const matchedResult = resultArr[0];
                    const reqId = matchedResult?.reqId || matchedResult?.employeeData?.[0]?.transactionId || '';

                    yield put({
                        type: overtimeConstants.CONFIRM_DECISION_SUCCESS,
                        employeeId,
                        shiftName,
                        decision,
                        lastTimeStamp,
                        reqId
                    });
                } else {
                    throw new Error(response?.data?.message || 'Failed to assign shift. Please try again.');
                }

            yield put({ type: overtimeConstants.SHOW_OVERTIME_LOADER, data: false });
            break;
        } catch (error) {
            yield put({
                type: overtimeConstants.CONFIRM_DECISION_FAILED,
                employeeId,
                shiftName,
                data: error.message || 'Failed to confirm decision. Please try again.'
            });
            
            yield put({
                type: overtimeConstants.ADD_OT_SHIFT_FAILED,
                employeeId,
                shiftName,
                data: error.message || 'Failed to assign shift. Please try again.'
            });
            
            yield put({ type: overtimeConstants.SHOW_OVERTIME_LOADER, data: false });
            break;
        }
    }
}

export function* resolveOTWbFailedSaga(action) {
    const { employeeId, shiftName, resolution } = action;
    try {
        yield put({ type: overtimeConstants.SHOW_OVERTIME_LOADER, data: true });

        const response = yield call(resolveWbFailed, {
            offerId: resolution.offerId,
            employeeId: resolution.employeeId,
            workbrainRequestId: resolution.workbrainRequestId,
            resolveAction: resolution.resolveAction,
            resolveComments: resolution.resolveComments,
        });

        if (response?.status === 200) {
            yield put({
                type: overtimeConstants.RESOLVE_OT_WB_FAILED_SUCCESS,
                employeeId,
                shiftName,
                resolveAction: resolution.resolveAction,
            });
        } else {
            yield put({
                type: overtimeConstants.RESOLVE_OT_WB_FAILED_FAILED,
                employeeId,
                shiftName,
                data: response?.data?.message || 'Failed to resolve WB Failed request.',
            });
        }

        yield put({ type: overtimeConstants.SHOW_OVERTIME_LOADER, data: false });
    } catch (error) {
        yield put({
            type: overtimeConstants.RESOLVE_OT_WB_FAILED_FAILED,
            employeeId,
            shiftName,
            data: error.message || 'Failed to resolve WB Failed request.',
        });
        yield put({ type: overtimeConstants.SHOW_OVERTIME_LOADER, data: false });
    }
}

export function* getOTSkillsDataSaga(data) {
    for(let i = 0; i < MAX_RETRIES; i++) {
        try {
            const response = yield call(
                getOTSkillsData,
                data.params,
            )
            
            if (response?.status === 200) {
                yield put(actions.getOTSkillsDataSuccess(true))
                const skillsData = response.data?.otskills || response.data?.skills || response.data || [];
                yield put(actions.getOTSkillsResponse(skillsData))
                return; // Success - exit the function
            } else {
                console.log("ERROR: " + response?.status);
                if (i === MAX_RETRIES - 1) {
                    yield put(actions.getOTSkillsDataSuccess(false))
                } else {
                    yield delay(RETRY_DELAY);
                }
            }
        }
        catch (error) {
            console.log("ERROR: " + error);
            if (i === MAX_RETRIES - 1) {
                // Last retry attempt failed
                yield put(actions.getOTSkillsDataSuccess(false))
            } else {
                yield delay(RETRY_DELAY);
            }
        }
    }
}

export function* advanceToNextRequirementSaga() {
    try {
        const callSheetData = yield select((state) => state.OvertimeReducer.callSheetData);
        const requestParams = callSheetData?.requestParams;
        const session = callSheetData?.session;
        const sessionToken = callSheetData?.sessionToken;
        const currentShift = callSheetData?.callSheetWorkingShift?.current || '';

        if (!requestParams || !currentShift) {
            return;
        }

        // Filter out the fulfilled requirement by matching shiftType to current shift name
        const updatedShiftsReqs = (requestParams.shiftsReqs || []).filter(
            (req) => (req.shiftType || '').toUpperCase() !== currentShift.toUpperCase()
        );

        const updatedParams = {
            ...requestParams,
            shiftsReqs: updatedShiftsReqs,
            sessionToken,
            isCurrentCompleted: false
        };

        // Reset fulfillment flag before fetching
        yield put({ type: overtimeConstants.RESET_SLOT_FULFILLED });

        // Re-fetch callsheet with remaining requirements
        yield put({ type: overtimeConstants.GET_OVERTIME_DATA, params: updatedParams });
    } catch (error) {
        console.error('Error advancing to next requirement:', error);
    }
}

/**
 * Fetches the VOT OT report for a given station, department, and date.
 * Dispatches GET_VOT_REPORT_SUCCESS with the report data on success,
 * or GET_VOT_REPORT_FAILED on error.
 */
export function* getVotReportSaga(action) {
    try {
        const response = yield call(getVotReportData, action.params);
        if (response?.status === 200) {
            yield put({
                type: overtimeConstants.GET_VOT_REPORT_SUCCESS,
                data: response.data,
            });
        } else {
            yield put({
                type: overtimeConstants.GET_VOT_REPORT_FAILED,
                data: response?.data?.message || 'Failed to fetch VOT report',
            });
        }
    } catch (error) {
        yield put({
            type: overtimeConstants.GET_VOT_REPORT_FAILED,
            data: error.message || 'Failed to fetch VOT report',
        });
    }
}

/**
 * Silent background poll of the callsheet API to refresh employee statuses
 * and pendingCount without showing the full-page loader.
 */
export function* pollCallsheetSaga() {
    try {
        const callSheetData = yield select((state) => state.OvertimeReducer.callSheetData);
        const requestParams = callSheetData?.requestParams;
        const session = callSheetData?.session;
        const sessionToken = callSheetData?.sessionToken;

        if (!requestParams) {
            return;
        }

        const response = yield call(getCallsheet, {
            ...requestParams,
            session,
            sessionToken,
            isCurrentCompleted: false,
        });
        yield put({
            type: overtimeConstants.POLL_CALLSHEET_SUCCESS,
            data: response.data,
        });
    } catch (error) {
        // Silent failure — polling will retry on next interval
        console.error('Poll callsheet failed:', error);
    }
}

/**
 * Polls /addOTShift/status with pending reqIds to check WB confirmation status.
 * Once all reqIds are resolved, triggers a final /callsheet poll to confirm
 * wbFailedCount === 0 and sync statuses before enabling Continue.
 */
export function* pollWbStatusSaga() {
    try {
        const overtimeState = yield select((state) => state.OvertimeReducer);
        const callSheetData = overtimeState?.callSheetData;
        const shiftSignups = overtimeState?.shiftSignups;
        const currentShift = callSheetData?.callSheetWorkingShift?.current || '';

        if (!currentShift || !shiftSignups?.byId) return;

        // Collect reqIds from signups with status ACCEPT (WB_PENDING) for the current shift
        const pendingReqIds = [];
        Object.values(shiftSignups.byId).forEach((signup) => {
            if ((signup.shiftName || '').toUpperCase() !== currentShift.toUpperCase()) return;
            const status = (signup.status || '').toUpperCase();
            if (status === 'ACCEPT' && signup.wbStatus !== true) {
                const id = signup.reqId || signup.transactionId || '';
                if (id) pendingReqIds.push(id);
            }
        });

        if (pendingReqIds.length === 0) {
            try {
                const requestParams = callSheetData?.requestParams;
                const sessionToken = callSheetData?.sessionToken;
                if (requestParams) {
                    const callsheetResponse = yield call(getCallsheet, {
                        ...requestParams,
                        sessionToken,
                        isCurrentCompleted: false,
                    });
                    yield put({
                        type: overtimeConstants.POLL_CALLSHEET_SUCCESS,
                        data: callsheetResponse.data,
                    });
                }
            } catch (e) {
                console.error('Callsheet refresh failed with no pending reqIds:', e);
            }
            yield put({ type: overtimeConstants.POLL_WB_STATUS_SUCCESS, results: {}, allResolved: true });
            return;
        }

        const response = yield call(checkOTShiftStatus, pendingReqIds.join(','));
        const results = Array.isArray(response?.data?.result) ? response.data.result : [];

        // Build status map: reqId → { status, comment }
        const statusMap = {};
        results.forEach((r) => {
            if (r.reqId) {
                statusMap[String(r.reqId)] = { status: r.status, comment: r.comment || '' };
            }
        });

        // Check if all pending reqIds have been resolved (not still pending)
        const allResolved = pendingReqIds.every((id) => {
            const result = statusMap[String(id)];
            return result && (result.status === 'APPLIED' || result.status === 'WB_SUCCESS' || result.status === 'WB_FAILURE');
        });

        if (allResolved) {
            // Final callsheet poll to sync all statuses and confirm wbFailedCount
            try {
                const requestParams = callSheetData?.requestParams;
                const sessionToken = callSheetData?.sessionToken;
                if (requestParams) {
                    const callsheetResponse = yield call(getCallsheet, {
                        ...requestParams,
                        sessionToken,
                        isCurrentCompleted: false,
                    });
                    yield put({
                        type: overtimeConstants.POLL_CALLSHEET_SUCCESS,
                        data: callsheetResponse.data,
                    });
                }
            } catch (e) {
                console.error('Final callsheet poll failed:', e);
            }
        }

        yield put({
            type: overtimeConstants.POLL_WB_STATUS_SUCCESS,
            results: statusMap,
            allResolved,
        });
    } catch (error) {
        console.error('Poll WB status failed:', error);
        yield put({
            type: overtimeConstants.POLL_WB_STATUS_SUCCESS,
            results: {},
            allResolved: false,
        });
    }
}

/**
 * Fire-and-forget POST to call-log API.
 * Logs actions (button clicks) and call entries.
 */
export function* postCallLogSaga(action) {
    try {
        yield call(postCallLog, action.data);
        yield put({ type: overtimeConstants.POST_CALL_LOG_SUCCESS });
    } catch (error) {
        // Silent failure — logging should not block the user
        console.error('Post call log failed:', error);
        yield put({ type: overtimeConstants.POST_CALL_LOG_FAILED });
    }
}

/**
 * Fetches call log entries for a specific employee from the API.
 */
export function* getCallLogSaga(action) {
    try {
        const response = yield call(getCallLog, { votId: action.votId, empId: action.empId });
        yield put({
            type: overtimeConstants.GET_CALL_LOG_SUCCESS,
            employeeId: action.empId,
            data: response.data,
        });
    } catch (error) {
        console.error('Get call log failed:', error);
        yield put({ type: overtimeConstants.GET_CALL_LOG_FAILED, data: error.message });
    }
}
